import { format } from 'date-fns';
import addDaysFns from 'date-fns/addDays';
import addHoursFns from 'date-fns/addHours';
import addMinutes from 'date-fns/addMinutes';
import endOfDay from 'date-fns/endOfDay';
import dateFnsFormat from 'date-fns/format';
import isBeforeFns from 'date-fns/isBefore';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import subDaysFns from 'date-fns/subDays';
import subHoursFns from 'date-fns/subHours';
import subMinutesFns from 'date-fns/subMinutes';

export const formatDateObj = (
  date: Date | null,
  format = `yyyy-MM-dd'T'HH:mm:ss`
) => {
  if (!date) {
    return '';
  }
  return dateFnsFormat(date, format);
};

export const formatLocalTimeString = (
  date?: string | number | null,
  format = `yyyy-MM-dd'T'HH:mm:ss`
) => {
  if (!date) {
    return '';
  }
  return dateFnsFormat(new Date(date), format);
};

export const formatDate = (
  date?: Date | number | null,
  format = `yyyy-MM-dd'T'HH:mm:ss`
) => {
  try {
    if (!date) {
      return '';
    }
    return dateFnsFormat(date, format);
  } catch (e) {
    return '';
  }
};

export const formatDateUsShort = (
  date?: Date | number | null,
  format = `MM/dd/yy`
) => {
  if (!date) {
    return '';
  }
  return dateFnsFormat(date, format);
};

export const formatDateShort = (
  date?: Date | number | null,
  format = `yyyy-MM-dd`
) => {
  if (!date) {
    return '';
  }
  return dateFnsFormat(date, format);
};
export const formatDateTimeStringToMeridanTime = (
  dateString?: string | null,
  to = 'hh:mm a'
) => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  if (!date.valueOf()) {
    return '';
  }
  return dateFnsFormat(date, to);
};

export const formatDateWithNull = (
  date?: Date | number | string | null,
  format = `yyyy-MM-dd'T'HH:mm:ss`
) => {
  try {
    if (!date || date === '' || !new Date(date).valueOf()) {
      return null;
    }
    let parsedDate: Date | number;

    if (typeof date === 'string') {
      parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return null;
      }
    } else {
      parsedDate = date;
    }

    return formatDate(parsedDate, format);
  } catch (e) {
    return null;
  }
};

export const formatDateDayStart = (date?: Date | string | number | null) =>
  formatDateWithNull(date, `yyyy-MM-dd'T'00:00:00`);
export const formatDateDayEnd = (date?: Date | string | number | null) =>
  formatDateWithNull(date, `yyyy-MM-dd'T'23:59:59`);

export const formatDateTimeString = (
  dateString?: string | Date | null,
  to = 'EEE MM/dd/yyyy'
) => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  if (!date.valueOf()) {
    return '';
  }
  return dateFnsFormat(date, to);
};

export const validate24HrsTime = (time?: string) => {
  if (!time) {
    return false;
  }
  return isValid(parse(time, 'HH:mm', new Date()));
};

export const convert24HrsIntoMeridian = (
  time?: string | null,
  format = 'hh:mm a'
) => {
  try {
    if (!time) {
      return '';
    }
    const date = parse(time, 'HH:mm', new Date());
    return dateFnsFormat(date, format);
  } catch (e) {
    return '';
  }
};

export const convert24HrsIntoMeridianShort = (time?: string | null) => {
  if (!time) {
    return '';
  }
  const date = parse(time, 'HH:mm', new Date());
  return dateFnsFormat(date, 'ha');
};

export const formatISODateString = (
  isoDate?: string,
  format = 'EEE MM/dd/yyyy'
) => {
  if (!isoDate) {
    return '';
  }
  const date = parseISO(isoDate);
  if (!isValid(date)) {
    return '';
  }
  return dateFnsFormat(date, format);
};

export const convert24HrsToDate = (
  time?: string | null,
  baseDate?: string | Date
) => {
  if (!time) {
    return null;
  }
  const date = baseDate ? new Date(baseDate) : new Date();
  const parts = time.split(':') as string[];
  date.setHours(+parts[0]);
  date.setMinutes(+parts[1]);
  return date;
};

export const convertDateTo24Hrs = (date: Date) => {
  return formatDateObj(date, 'HH:mm');
};

export const getStartOfDay = (date?: Date) => {
  if (!date) {
    return '';
  }
  return formatDate(startOfDay(date));
};

export const getEndOfDay = (date?: Date) => {
  if (!date) {
    return '';
  }
  return formatDate(endOfDay(date));
};

export const subDays = (date: Date, days: number) => {
  return subDaysFns(date, days);
};

export const addDays = (date: Date, days: number) => {
  return addDaysFns(date, days);
};

export const sleeper = (timeout = 1000) =>
  new Promise((resolve) => setTimeout(resolve, timeout));

export const addHours = (date: Date, days: number) => {
  return addHoursFns(date, days);
};

export const subHours = (date: Date, days: number) => {
  return subHoursFns(date, days);
};
export const subMinutes = (date: Date, minutes: number) => {
  return subMinutesFns(date, minutes);
};
export const isBefore = (sDate: Date, eDate: Date) => {
  return isBeforeFns(sDate, eDate);
};

export const mergeDateAndTime = (date: Date, time: Date) => {
  const base = new Date(date);
  base.setHours(time.getHours());
  date.setMinutes(time.getMinutes());
  return formatDateObj(date, `yyyy-MM-dd'T'HH:mm:ss`);
};

export const withSafeDate = (date?: string | null) => {
  if (!date) {
    return null;
  }
  return new Date(date);
};

export const convertMinutesToHHmm = (minutes = 0) => {
  const paddedHours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const paddedMinutes = (minutes % 60).toString().padStart(2, '0');
  return `${paddedHours}:${paddedMinutes}`;
};

export const formatToDateTime = (isoDateStr: string): string | null => {
  if (!isoDateStr) return null;

  const date = new Date(isoDateStr);
  if (isNaN(date.getTime())) return null; // Invalid date

  const pad = (n: number): string => n.toString().padStart(2, '0');

  const month = pad(date.getMonth() + 1); // Months are 0-based
  const day = pad(date.getDate());
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${month}/${day}/${year} ${hours}:${minutes}`;
};

export const buildDateTimeWithCurrentTime = (
  dateStr?: string | null
): string => {
  try {
    if (!dateStr) return '';
    const base = new Date(dateStr);
    if (isNaN(base.getTime())) return '';
    const now = new Date();
    base.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
    return dateFnsFormat(base, `yyyy-MM-dd'T'HH:mm:ss`);
  } catch {
    return '';
  }
};

export const buildDateTimeFromTimeStr = (
  time24hr: string,
  baseDateTime: string
) => {
  const date = new Date(baseDateTime);
  const parts = time24hr.split(':');
  date.setHours(+parts[0]);
  date.setMinutes(+parts[1]);
  return formatDateObj(date, `yyyy-MM-dd'T'HH:mm:ss`);
};

export const buildDateObjectOrNull = (value: any) => {
  if (!value) {
    return null;
  }
  try {
    const date = new Date(value);
    if (!date.valueOf()) {
      return null;
    }
    return date;
  } catch (e) {
    return null;
  }
};

export const isValidDate = (
  date: Date | string | number | null | undefined
): boolean => {
  if (!date) return false;
  return !isNaN(new Date(date).getTime());
};

export const minutesToTimeString = (m?: number) => {
  try {
    if (m == null || m < 0 || m >= 1440) return '';
    const date = addMinutes(startOfDay(new Date()), m);
    if (!isValid(date)) return '';

    return format(date, 'hh:mm a').toUpperCase();
  } catch {
    return '';
  }
};
